import { useState, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Flex, Text } from '@mantine/core';
import { Table } from '@tanstack/react-table';
import { ArchivedBanner } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/TaxAndCompliance/styled.ts';
import { type DocumentWorkflowTemplateInfo } from '@dealstack/shared/lib/generated';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { CreateMasterDocumentDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/CreateMasterDocumentDrawer/CreateMasterDocumentDrawer.tsx';
import { MasterDocumentSettingsDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/MasterDocumentSettingsDrawer/MasterDocumentSettingsDrawer.tsx';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer';
import { DocumentViewerModalV2 } from '@dealstack/frontend/shared/components/DocumentViewerV2/DocumentViewerModalV2.tsx';
import { useContractsPermissions } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/hooks/useContractsPermissions.ts';
import { useMasterDocumentsTableState } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/MasterDocumentsTable/useMasterDocumentsTableState.ts';
import { useMasterDocumentsQuery } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useMasterDocumentsQuery.ts';
import { useBulkArchiveMasterDocuments } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useBulkArchiveMasterDocuments.ts';
import { useBulkDeleteMasterDocuments } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useBulkDeleteMasterDocuments.ts';
import { useMasterDocumentPreviewFile } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/api/useMasterDocumentPreviewFile.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import {
	createBulkActions,
	ContractBulkActionParams,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/DocumentWorkflowTemplateTable/createBulkActions';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { isMasterDocumentsCreateState } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/sendDocumentsRoundTrip.utils';

export const MasterDocumentsTable = () => {
	const tMipManage = useTranslationMipManage();
	// Preview downloads through a deal-scoped endpoint, so it is unavailable on an
	// organisation route; the hook's own queries stay disabled without these.
	const { dealId, mipId, organizationId } = useParams();
	const tableRef = useRef<HTMLDivElement | null>(null);
	const location = useLocation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const { hasPermission } = useContractsPermissions();

	// A `?create=1` round trip from the Send Documents modal opens the drawer immediately.
	const [showAddDrawer, setShowAddDrawer] = useState(() => searchParams.get('create') === '1');
	// Captured on the first render because it cannot survive one: the table writes its pagination
	// and sort into the URL on mount with `setSearchParams(..., { replace: true })`, and a replace
	// drops `location.state`. Reading it in `onCloseDrawer` would always find it gone, so the
	// round trip back to Send Documents never fired.
	const [createReturn] = useState(() =>
		isMasterDocumentsCreateState(location.state) ? location.state : null
	);
	const [showArchivedFiles, setShowArchivedFiles] = useState(false);
	const [confirmBulkDeleteOpened, setConfirmBulkDeleteOpened] = useState(false);
	const [pendingDeleteParams, setPendingDeleteParams] = useState<ContractBulkActionParams | null>(
		null
	);
	const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
	const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
	const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);

	const onCloseDrawer = useCallback(() => {
		setShowAddDrawer(false);
		// Arrived via the Send Documents modal's upload round trip — hand control back with
		// the stashed selection so it reopens, whether the drawer closed via cancel or create.
		if (createReturn) {
			navigate(createReturn.returnTo, {
				state: {
					reopenSendDocuments: true,
					sendDocumentsSelection: createReturn.sendDocumentsSelection,
				},
			});
			return;
		}
		// Ordinary close: drop a bookmarked/reloaded `?create=1` so it doesn't reopen the drawer.
		if (searchParams.has('create')) {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					next.delete('create');
					return next;
				},
				{ replace: true }
			);
		}
	}, [createReturn, navigate, searchParams, setSearchParams]);

	const onCloseSettingsDrawer = useCallback(() => {
		setSettingsDrawerOpen(false);
	}, []);

	const onClosePreview = useCallback(() => {
		setPreviewDocumentId(null);
	}, []);

	const handlePreview = useCallback((documentId: string) => {
		setPreviewDocumentId(documentId);
	}, []);

	const toggleArchivedFiles = useCallback(() => {
		setShowArchivedFiles((prev) => !prev);
	}, []);

	const { columns, mantineTableDataOperations, masterDocumentsRequestParams } =
		useMasterDocumentsTableState({
			showArchivedFiles,
			onPreview: handlePreview,
		});

	const { documentWorkflowTemplates, totalCount, isFetching, refetch } = useMasterDocumentsQuery(
		masterDocumentsRequestParams
	);

	const previewDocument = useMemo(
		() => documentWorkflowTemplates.find((doc) => doc.id === previewDocumentId),
		[documentWorkflowTemplates, previewDocumentId]
	);

	const {
		file: previewFile,
		fileName: previewFileName,
		fileExtension: previewFileExtension,
		isLoading: isLoadingPreviewFile,
	} = useMasterDocumentPreviewFile({
		document: previewDocument,
		dealId: defaultToEmptyString(dealId),
		programmeId: defaultToEmptyString(mipId),
		organizationId,
	});

	const refreshList = useCallback(() => {
		void refetch();
	}, [refetch]);
	const bulkArchiveMutation = useBulkArchiveMasterDocuments(refreshList);
	const bulkDeleteMutation = useBulkDeleteMasterDocuments(refreshList);

	const handleBulkArchive = useCallback(
		(params: ContractBulkActionParams) => {
			bulkArchiveMutation.mutate({
				selectedIds: params.selectedIds,
				excludedIds: params.excludedIds,
				filter: params.filter,
				search: params.search,
				archived: params.archived ?? true,
			});
		},
		[bulkArchiveMutation]
	);

	const initiateBulkDelete = useCallback((params: ContractBulkActionParams) => {
		setPendingDeleteParams(params);
		setConfirmBulkDeleteOpened(true);
	}, []);

	const handleConfirmBulkDelete = useCallback(() => {
		if (pendingDeleteParams) {
			bulkDeleteMutation.mutate(
				{
					selectedIds: pendingDeleteParams.selectedIds,
					excludedIds: pendingDeleteParams.excludedIds,
					filter: pendingDeleteParams.filter,
					search: pendingDeleteParams.search,
				},
				{
					onSuccess: () => {
						setConfirmBulkDeleteOpened(false);
						setPendingDeleteParams(null);
					},
				}
			);
		}
	}, [bulkDeleteMutation, pendingDeleteParams]);

	const bulkActionsFactory = useCallback(
		(table: Table<DocumentWorkflowTemplateInfo>) =>
			createBulkActions(table, {
				showArchivedFiles,
				isFetching:
					isFetching || bulkArchiveMutation.isPending || bulkDeleteMutation.isPending,
				hasEditorPermission: hasPermission,
				filter: masterDocumentsRequestParams.filter,
				search: masterDocumentsRequestParams.search,
				tMipManage,
				toggleArchivedFiles,
				onBulkArchive: handleBulkArchive,
				onBulkDelete: initiateBulkDelete,
			}),
		[
			showArchivedFiles,
			isFetching,
			bulkArchiveMutation.isPending,
			bulkDeleteMutation.isPending,
			hasPermission,
			masterDocumentsRequestParams.filter,
			masterDocumentsRequestParams.search,
			tMipManage,
			toggleArchivedFiles,
			handleBulkArchive,
			initiateBulkDelete,
		]
	);

	return (
		<Flex direction="column" p="1rem" gap="1rem">
			{hasPermission && (
				<Flex justify="flex-end" gap="1rem">
					<Button
						onClick={() => setShowAddDrawer(true)}
						leftSection={<Icon variant="add" size="lg" />}
					>
						{tMipManage('master_documents.text.button_add')}
					</Button>
				</Flex>
			)}
			{showArchivedFiles && (
				<ArchivedBanner justify="space-between" align="center" p="1rem">
					<Text size="B6Bold" c="dark.8">
						{tMipManage('master_documents.text.viewing_archived')}
					</Text>
					<Button
						onClick={() => setShowArchivedFiles(false)}
						variant="transparent"
						c="blue.6"
						p={0}
					>
						{tMipManage('master_documents.text.exit_archive_view')}
					</Button>
				</ArchivedBanner>
			)}
			<MantineTable<DocumentWorkflowTemplateInfo>
				getRowId={(row) => row.id}
				renderBulkActionsMenu={hasPermission}
				actions={bulkActionsFactory}
				mantineTableBodyRowProps={({ row }) => ({
					id: row.id,
					style: {
						cursor: 'pointer',
					},
					onClick: () => {
						setSelectedDocumentId(row.original.id);
						setSettingsDrawerOpen(true);
					},
				})}
				tableRef={tableRef}
				columns={columns}
				isLoading={isFetching}
				dataOperations={mantineTableDataOperations}
				data={documentWorkflowTemplates}
				initialState={{
					columnVisibility: {
						id: false,
					},
				}}
				rowCount={totalCount}
				enableRowActions={false}
				enablePagination={true}
				manualPagination={true}
				enableRowSelection={true}
			/>
			<CreateMasterDocumentDrawer
				opened={showAddDrawer}
				onClose={onCloseDrawer}
				refreshList={refreshList}
			/>
			<MasterDocumentSettingsDrawer
				opened={settingsDrawerOpen}
				documentId={selectedDocumentId}
				onClose={onCloseSettingsDrawer}
			/>
			<DocumentViewerModalV2
				opened={!!previewDocumentId}
				onClose={onClosePreview}
				file={previewFile}
				fileName={previewFileName}
				fileExtension={previewFileExtension}
				isLoading={isLoadingPreviewFile}
				showBackButton={false}
			/>
			<ConfirmDeleteDrawer
				opened={confirmBulkDeleteOpened}
				onClose={() => {
					setConfirmBulkDeleteOpened(false);
					setPendingDeleteParams(null);
				}}
				title={tMipManage('master_documents.delete_drawer.title')}
				alertTitle={tMipManage('master_documents.delete_drawer.alert_title')}
				alertDescription={tMipManage('master_documents.delete_drawer.alert_description')}
				hideAlertIcon={true}
				confirmDeleteText={tMipManage('master_documents.delete_drawer.confirm_word')}
				wordMismatchErrorMessage={tMipManage(
					'master_documents.delete_drawer.word_mismatch'
				)}
				inputLabel={tMipManage('master_documents.delete_drawer.input_label')}
				inputPlaceholder={tMipManage('master_documents.delete_drawer.input_placeholder')}
				deleteBtnText={tMipManage('master_documents.delete_drawer.delete_button', {
					count:
						pendingDeleteParams?.count ?? pendingDeleteParams?.selectedIds?.length ?? 0,
				})}
				cancelBtnText={tMipManage('master_documents.delete_drawer.cancel_button')}
				deleteSuccess={bulkDeleteMutation.isSuccess}
				deleteLoading={bulkDeleteMutation.isPending}
				handleDelete={handleConfirmBulkDelete}
			/>
		</Flex>
	);
};
